import {Alert, Button, ButtonGroup, Dialog} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

interface ChangeVehicleDialogProps {
  isConfirmChangeVehicleOpen: boolean;
  toggleIsConfirmChangeVehicleOpen: () => void;
  /** Replacing from stock: the existing route, which swaps the vehicle in one step. */
  onChangeVehicleConfirm: () => void;
  /** Building a new vehicle from the catalogue. Omitted when that route is unavailable. */
  onConfigureNewVehicle?: () => void;
  /** Committing a configuration that already exists. Omitted when that route is unavailable. */
  onUseExistingConfiguration?: () => void;
}

export function ChangeVehicleDialog(props: ChangeVehicleDialogProps) {
  const hasNewVehicleRoutes =
    isNotNil(props.onConfigureNewVehicle) || isNotNil(props.onUseExistingConfiguration);

  return (
    <Dialog
      data-testid={testIds.businessCase.selling('change-vehicle-confirm')}
      isOpen={props.isConfirmChangeVehicleOpen}
      onClose={props.toggleIsConfirmChangeVehicleOpen}
      size="small"
      title={i18n.t`page.businessCase.labels.confirmChangeVehicle`}
    >
      {hasNewVehicleRoutes ? (
        <VStack spacing={4}>
          <VStack spacing={2}>
            <Button
              variant="primary"
              onClick={props.onChangeVehicleConfirm}
              title={t('page.businessCase.actions.changeVehicleFromStock', 'Select from stock')}
              data-testid={testIds.businessCase.selling('change-vehicle-stock')}
            />
            <Show when={isNotNil(props.onConfigureNewVehicle)}>
              <Button
                variant="secondary"
                onClick={() => props.onConfigureNewVehicle?.()}
                title={t('entity.newVehicle.actions.configure', 'Configure new vehicle')}
                data-testid={testIds.businessCase.selling('change-vehicle-configure')}
              />
            </Show>
            <Show when={isNotNil(props.onUseExistingConfiguration)}>
              <Button
                variant="secondary"
                onClick={() => props.onUseExistingConfiguration?.()}
                title={t(
                  'entity.newVehicle.actions.useExistingConfiguration',
                  'Use existing configuration'
                )}
                data-testid={testIds.businessCase.selling('change-vehicle-configuration')}
              />
            </Show>
          </VStack>
          {/* The swap happens when the new vehicle is confirmed, not now - worth saying, because
              the configurator route leaves and comes back before anything changes. */}
          <Alert
            variant="info"
            type="inline"
            message={t(
              'page.businessCase.labels.changeVehicleReplacesOnConfirm',
              'The current vehicle stays on this business case until you confirm the new one.'
            )}
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              onClick={props.toggleIsConfirmChangeVehicleOpen}
              title={i18n.t('general.actions.cancel')}
            />
          </ButtonGroup>
        </VStack>
      ) : (
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            onClick={props.toggleIsConfirmChangeVehicleOpen}
            title={i18n.t('general.labels.no')}
          />
          <Button
            variant="primary"
            onClick={props.onChangeVehicleConfirm}
            title={i18n.t('general.labels.yes')}
          />
        </ButtonGroup>
      )}
    </Dialog>
  );
}
